
import { supabase } from "@/integrations/supabase/client";

// Cache to store CPV codes we've already looked up
const cpvCache: Record<string, string> = {};

/**
 * Fetches the description for a CPV code from the database
 * @param cpvCode The CPV code to look up (can include suffix like "12345678-9")
 * @returns The description or "Unknown" if not found
 */
export const getCpvDescription = async (cpvCode: string): Promise<string> => {
  // Clean the CPV code - extract just the numeric part before any dash
  const cleanCpvCode = cpvCode.split('-')[0].trim();
  
  // If not a valid number, return Unknown
  if (!cleanCpvCode || isNaN(parseInt(cleanCpvCode))) {
    return "Unknown";
  }

  // Check cache first
  if (cpvCache[cleanCpvCode]) {
    return cpvCache[cleanCpvCode];
  }

  try {
    // Query the CPV table for the description
    const { data, error } = await supabase
      .from("CPV Table")
      .select("Beskrivelse")
      .eq("CPV", parseInt(cleanCpvCode))
      .single();

    if (error || !data) {
      console.error("Error fetching CPV description:", error);
      return "Unknown";
    }

    // Store in cache and return
    cpvCache[cleanCpvCode] = data.Beskrivelse;
    return data.Beskrivelse;
  } catch (error) {
    console.error("Error in getCpvDescription:", error);
    return "Unknown";
  }
};

/**
 * Gets a formatted CPV code with description
 * @param cpvCode The CPV code
 * @returns Formatted string with code and description
 */
export const getFormattedCpvWithDescription = async (cpvCode: string): Promise<string> => {
  const description = await getCpvDescription(cpvCode);
  return `${cpvCode} - ${description}`;
};
